import React, { useState } from "react"

import NetworkLeaderboardTabs from "../NetworkLeaderboardTabs"
import Scorecard from "./Scorecard"
import Leaderboard from "./Leaderboard"
import Skins from "./Skins"
import { TeamRound, TeamRoundPlayer, Hole, HoleSkin, Round, AssetMap } from "../types"

interface TabsProps {
  holeIndex: number
  round: Round
  teamRounds: TeamRound[]
  pairing: TeamRound[]
  teamRound: TeamRound
  currentUserId: number
  skins: Map<Hole, HoleSkin>
  assets: AssetMap
  editableTeamRoundId?: number
  onScoreCellPress?: (holeIndex: number, player: TeamRoundPlayer) => void
}

export default function Tabs({
  holeIndex,
  round,
  teamRounds,
  pairing,
  teamRound,
  currentUserId,
  skins,
  assets,
  editableTeamRoundId,
  onScoreCellPress
}: TabsProps) {
  const { numberOfHoles, holes } = round
  const startsAt = new Date(round.startsAt)
  const showScorecard = startsAt < new Date() && !!teamRound
  const [activeTab, setActiveTab] = useState(showScorecard ? "scorecard" : "leaderboard")

  return (
    <div className="card">
      <div className="container">
        <Header activeTab={activeTab} setActiveTab={setActiveTab} scorecard={showScorecard} round={round} />
        <Content
          activeTab={activeTab}
          round={round}
          numberOfHoles={numberOfHoles}
          holes={holes}
          holeIndex={holeIndex}
          currentUserId={currentUserId}
          teamRound={teamRound}
          teamRounds={teamRounds}
          pairing={pairing}
          skins={skins}
          assets={assets}
          editableTeamRoundId={editableTeamRoundId}
          onScoreCellPress={onScoreCellPress}
        />
      </div>
    </div>
  )
}

interface HeaderProps {
  activeTab: string
  setActiveTab(activeTab: string): void
  scorecard: boolean
  round: Round
}

export function Header({ activeTab, setActiveTab, scorecard, round }: HeaderProps) {
  const topgolf = round.league.leagueType === "topgolf"
  return (
    <div className={scorecard ? "spark-tab-bar three-up" : "spark-tab-bar"}>
      <div className="inner">
        {scorecard ? (
          <a className={activeTab === "scorecard" ? "active" : ""} onClick={() => setActiveTab("scorecard")}>
            Scorecard
          </a>
        ) : null}
        <a className={activeTab === "leaderboard" ? "active" : ""} onClick={() => setActiveTab("leaderboard")}>
          {topgolf ? round.league.courseShortLabel : "Leaderboard"}
        </a>
        {topgolf ? (
          round.networkRound && (
            <a className={activeTab === "network" ? "active" : ""} onClick={() => setActiveTab("network")}>
              All Topgolf
            </a>
          )
        ) : (
          <a className={activeTab === "skins" ? "active" : ""} onClick={() => setActiveTab("skins")}>
            Skins
          </a>
        )}
      </div>
    </div>
  )
}

interface ContentProps {
  activeTab: string
  round: Round
  numberOfHoles: number
  holes: Hole[]
  holeIndex: number
  currentUserId: number
  teamRound: TeamRound
  teamRounds: TeamRound[]
  pairing: TeamRound[]
  skins: Map<Hole, HoleSkin>
  assets: AssetMap
  editableTeamRoundId?: number
  onScoreCellPress?: (holeIndex: number, player: TeamRoundPlayer) => void
}

function Content({
  activeTab,
  round,
  numberOfHoles,
  holes,
  holeIndex,
  currentUserId,
  teamRound,
  teamRounds,
  pairing,
  skins,
  assets,
  editableTeamRoundId,
  onScoreCellPress
}: ContentProps) {
  switch (activeTab) {
    case "scorecard":
      return (
        <>
          {pairing.map((tr) => {
            const editable = editableTeamRoundId != null && tr.id === editableTeamRoundId
            return (
              <Scorecard
                key={tr.id}
                numberOfHoles={numberOfHoles}
                holes={holes}
                holeIndex={holeIndex}
                teamRound={tr}
                onScoreCellPress={editable ? onScoreCellPress : undefined}
              />
            )
          })}
        </>
      )
    case "network":
      return round.league.leagueType === "topgolf" && round.networkRound ? (
        <div className="network-competition">
          {React.createElement(
            "turbo-frame",
            {
              id: `network-leaderboard-${round.networkRound.sqid}`,
              src: `/app/nr/${round.networkRound.sqid}?embedded=1&league_id=${round.league.id}`,
              "data-turbo": "true"
            },
            <p>Loading leaderboard…</p>
          )}
        </div>
      ) : null
    case "leaderboard": {
      const leaderboard = (
        <Leaderboard
          currentUserId={currentUserId}
          round={round}
          teamRound={teamRound}
          teamRounds={teamRounds}
          assets={assets}
        />
      )
      return round.league.leagueType === "topgolf" ? (
        leaderboard
      ) : (
        <NetworkLeaderboardTabs round={round}>{leaderboard}</NetworkLeaderboardTabs>
      )
    }
    case "skins":
      return <Skins skins={skins} />
  }
}
